// LineSelectionPopover — the multi-select line popover from the lines mockup
// (#1830): a vertical list of lines (color dot + name), a «Без линии» clear
// action and Применить/Отменить buttons. Selection is local until Apply.
// Used by the editor popovers (block tag + text selection) and the plan cards.

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { ClickAwayListener, Popper, type PopperProps } from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLineColor } from '@/hooks';
import type { ILine } from '@/types';
import { hexAlpha } from '@/utils/lineColorUtils';

interface LineSelectionPopoverProps {
    lines: ILine[];
    selectedLineIds: number[];
    anchorEl: PopperProps['anchorEl'];
    onApply: (lineIds: number[]) => void;
    onClose: () => void;
    /** «Создать линию» row (shown when provided): receives the popover's
     *  CURRENT local selection so the caller can merge it with the freshly
     *  created line when applying. The row exists so a project with no lines
     *  yet isn't a dead end — the picker itself offers creating the first one. */
    onCreateLine?: (currentSelection: number[]) => void;
}

export const LineSelectionPopover = memo(
    ({ lines, selectedLineIds, anchorEl, onApply, onClose, onCreateLine }: LineSelectionPopoverProps) => {
        const { t } = useTranslation();
        const getLineColor = useGetLineColor();
        const [selection, setSelection] = useState<number[]>(selectedLineIds);

        const toggleLine = useCallback((lineId: number) => {
            setSelection(prev => (prev.includes(lineId) ? prev.filter(id => id !== lineId) : [...prev, lineId]));
        }, []);

        const handleClearAll = useCallback(() => {
            setSelection([]);
        }, []);

        const handleApply = useCallback(() => {
            onApply(selection);
        }, [selection, onApply]);

        const sortedLines = [...lines].sort((a, b) => a.code.localeCompare(b.code));

        return (
            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                className="z-[1300]"
            >
                <ClickAwayListener onClickAway={onClose}>
                    <div
                        role="presentation"
                        className="min-w-[220px] max-w-[300px] rounded-xl bg-c1 p-2 shadow-md dark:bg-c37"
                        onMouseDown={e => e.preventDefault()}
                        // React events bubble through the portal into the host component
                        // tree (e.g. a card's click-to-edit Typography) — keep them here.
                        onClick={e => e.stopPropagation()}
                        data-line-popup
                    >
                        <div className="mb-1 flex max-h-[260px] flex-col overflow-y-auto">
                            {sortedLines.map(line => {
                                const color = getLineColor(line.color);
                                const isSelected = selection.includes(line.id);

                                return (
                                    <button
                                        key={line.id}
                                        type="button"
                                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-text-primary hover:bg-c5 dark:text-c1 dark:hover:bg-c16"
                                        style={isSelected ? { backgroundColor: hexAlpha(color, 0.12) } : undefined}
                                        onClick={() => toggleLine(line.id)}
                                    >
                                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                                        <span className="flex-1 truncate">
                                            {line.title?.trim() || `${t('lines.line-prefix')} ${line.code}`}
                                        </span>
                                        {isSelected && <CheckIcon style={{ fontSize: '14px', color }} />}
                                    </button>
                                );
                            })}
                            {/* Muted rows use c79/c11 (the LinesToolbar create-chip pair), NOT
                                text-text-secondary: that token is #FAFAFA in light mode — meant
                                for the always-dark paper surfaces — and reads white-on-white on
                                this popover's bg-c1. */}
                            {onCreateLine && (
                                <button
                                    type="button"
                                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-c79 hover:bg-c5 dark:text-c11 dark:hover:bg-c16"
                                    onClick={() => onCreateLine(selection)}
                                >
                                    <AddIcon style={{ fontSize: '14px' }} />
                                    <span>{t('lines.create-button')}</span>
                                </button>
                            )}
                            <button
                                type="button"
                                className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-c79 hover:bg-c5 dark:text-c11 dark:hover:bg-c16"
                                onClick={handleClearAll}
                            >
                                <LinkOffIcon style={{ fontSize: '14px' }} />
                                <span>{t('lines.no-line')}</span>
                            </button>
                        </div>
                        <div className="flex gap-2 border-t border-c4 pt-2 dark:border-c62">
                            <button
                                type="button"
                                className="flex-1 cursor-pointer rounded-lg bg-error py-1.5 text-sm font-medium text-error-contrast"
                                onClick={handleApply}
                            >
                                {t('lines.apply')}
                            </button>
                            <button
                                type="button"
                                className="cursor-pointer rounded-lg border border-c4 bg-transparent px-4 py-1.5 text-sm text-text-primary dark:border-c62 dark:text-c1"
                                onClick={onClose}
                            >
                                {t('lines.cancel')}
                            </button>
                        </div>
                    </div>
                </ClickAwayListener>
            </Popper>
        );
    },
);

LineSelectionPopover.displayName = 'LineSelectionPopover';
